import * as React from 'react';

import { connect } from 'react-redux';

import { Layout, Spin, Row, Col, Card, Table } from 'antd';

import { getCurrency, nullSafeGetOrElse, sortTreeData } from '../../utils/DataAccessUtils';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { withRouter } from 'react-router';
import AnalyticsControlBar from '../analytics_control_bar/AnalyticsControlBar';
import { clearVendorFilters, updateVendorFilters } from '../../actions/spend_analytics_actions';
import {
	fetchSpendCostsByVendor,
	fetchSpendCostsByMonth,
} from '../../thunks/spend_reportings_thunks';
import { debounce } from '../../utils/PerformanceUtils';
import { EmptyState } from '../empty_state/EmptyState';
import { SpendOverTimeChart } from '../spend_analytics_overview_page/SpendOverTimeChart';
import { SpendBreakdownSection } from '../spend_analytics_overview_page/SpendBreakdownSection';
import { buildSpendBreakdownColumns } from '../spend_analytics_overview_page/SpendBreakdownColumns';
import { getActiveSpendSources, SPEND_SOURCES } from '../../utils/AnalyticsUtils';
import { exportSpendBySourceCsv } from '../../utils/AnalyticsBreakdownExportUtils';
import { FILTER_CONFIG_NAMES } from '../../utils/DataConstants';
import { exportAnalyticsPageAsPdf } from '../../utils/AnalyticsPdfExportUtils';

require('./SpendAnalyticsOverviewVendorsPage.less');
const { Content } = Layout;
const isEqual = require('fast-deep-equal');

// Suppliers only attribute invoiced spend — internal (self-performed) sources don't map to a supplier.
const INVOICE_ONLY_SOURCES = SPEND_SOURCES.filter((source) => source.key === 'invoiceSpend');

const CSV_TITLE = 'Spend Analytics by Suppliers';

class SpendAnalyticsOverviewVendorsPage extends React.Component<any, any> {
	state = {
		initialLoading: true,
		printing: false,
	};
	debouncedCallback = debounce(
		() => {
			const { fetchSpendData, fetchTimeSeriesSpendData, analytics } = this.props;
			const { initialLoading } = this.state;
			const promise1 = fetchSpendData(analytics.vendorFilters);
			const promise2 = fetchTimeSeriesSpendData(analytics.vendorFilters);
			if (initialLoading) {
				Promise.all([promise1, promise2]).then(() => this.setState({ initialLoading: false }));
			}
		},
		500,
		false
	);

	// Drop pagination so every row renders, let the table re-render, then capture the full table.
	handlePrint = () =>
		this.setState({ printing: true }, () =>
			setTimeout(
				() =>
					exportAnalyticsPageAsPdf(
						'.analyticsOverviewVendorsPage',
						'spend-analytics-vendors-' + new Date().toISOString().slice(0, 10) + '.pdf'
					).finally(() => this.setState({ printing: false })),
				400
			)
		);

	handleExportCsv = () => {
		const { currentUser } = this.props;
		const currency = getCurrency({ currentUser });
		const fieldRows = this.getDisplayData();
		exportSpendBySourceCsv({
			title: CSV_TITLE,
			categoryColumnLabel: 'Supplier',
			rows: fieldRows,
			activeSources: getActiveSpendSources(fieldRows, INVOICE_ONLY_SOURCES),
			formatValue: (amount) => currency.symbolFormat(amount, 0),
		});
	};

	componentDidMount() {
		const { fetchSpendData, fetchTimeSeriesSpendData, analytics } = this.props;
		const { initialLoading } = this.state;
		if (analytics.vendorFilters.startDate && analytics.vendorFilters.endDate) {
			const promise1 = fetchSpendData(analytics.vendorFilters);
			const promise2 = fetchTimeSeriesSpendData(analytics.vendorFilters);
			if (initialLoading) {
				Promise.all([promise1, promise2]).then(() => this.setState({ initialLoading: false }));
			}
		}
	}
	componentDidUpdate(prevProps) {
		const { analytics } = this.props;
		if (!isEqual(prevProps.analytics.vendorFilters, analytics.vendorFilters)) {
			this.debouncedCallback();
		}
	}

	getDisplayData = () =>
		sortTreeData(
			nullSafeGetOrElse('props.analytics.spendCostsByVendor.data', this, []),
			(a, b) =>
				parseFloat(nullSafeGetOrElse('invoiceSpend', b, 0)) -
				parseFloat(nullSafeGetOrElse('invoiceSpend', a, 0))
		);

	render() {
		const {
			saveVendorFilters,
			clearAllVendorFilters,
			analytics,
			accountActivated,
			currentUser,
			userType,
		} = this.props;
		const { initialLoading, printing } = this.state;
		const { spendCostsByVendor, spendCostsByMonth, vendorFilters } = analytics;

		const spendByVendorData = this.getDisplayData();
		const currency = getCurrency({ currentUser });

		// Suppliers tab is invoice-only for both table and charts.
		const breakdownActiveSources = getActiveSpendSources(spendByVendorData, INVOICE_ONLY_SOURCES);

		// Reuse the shared per-source column builder (invoice-only here) so the linked-amount cell,
		// drill-down, and zero/grey styling stay identical to the Problem Types / Locations tabs.
		const spendByVendorColumns = [
			{
				title: 'Supplier',
				dataIndex: 'fieldName',
				render: (text) => (
					<Ellipsis tooltip={true} style={{ display: 'inline' }} length={28}>
						{text}
					</Ellipsis>
				),
			},
			...buildSpendBreakdownColumns({
				activeSources: breakdownActiveSources,
				currency,
				userType,
				baseFilters: vendorFilters,
				dimensionFilterKey: 'supplierFacilityIds',
				fromTree: false,
			}),
		];

		return (
			<Content
				className="analyticsOverviewVendorsPage"
				style={{ padding: '0 0.5em', position: 'relative' }}
			>
				<div className="analytics-pdf-only" style={{ display: 'none' }}>
					<h2>Spend Analytics – Suppliers</h2>
				</div>
				{accountActivated ? (
					<div>
						<Row key={1} style={{ margin: '0.5em -8px' }} gutter={16}>
							<Col span={24}>
								<AnalyticsControlBar
									updateFilters={saveVendorFilters}
									clearFilters={clearAllVendorFilters}
									problemTypeIds={vendorFilters.problemTypeIds}
									woPriorityIds={vendorFilters.woPriorityIds}
									supplierFacilityIds={vendorFilters.supplierFacilityIds}
									locationIds={vendorFilters.locationIds}
									brandIds={vendorFilters.brandIds}
									startDate={vendorFilters.startDate}
									endDate={vendorFilters.endDate}
									isCapex={vendorFilters.isCapex}
									isPM={vendorFilters.isPM}
									workCreatedAtStartDate={vendorFilters.workCreatedAtStartDate}
									workCreatedAtEndDate={vendorFilters.workCreatedAtEndDate}
									workCompletedAtStartDate={vendorFilters.workCompletedAtStartDate}
									workCompletedAtEndDate={vendorFilters.workCompletedAtEndDate}
									areas={vendorFilters.areas}
									areaHierarchyNames={vendorFilters.areaHierarchyNames}
									showDownload={true}
									onDownload={this.handleExportCsv}
									showPrint={true}
									onPrint={this.handlePrint}
									filterConfig={{
										workCreatedAt: true,
										workCompletedAt: true,
										capEx: true,
										isSpendAnalytics: true,
										isPM: true,
										problemTypes: true,
										workOrderPriorities: true,
										[FILTER_CONFIG_NAMES.AREA]: true,
									}}
								/>
							</Col>
						</Row>
						{!spendCostsByMonth.loading &&
						!initialLoading &&
						(!spendByVendorData || spendByVendorData.length === 0) ? (
							<EmptyState
								height={undefined}
								headline={'No invoices found'}
								body={
									<div style={{ maxWidth: 488, textAlign: 'center' }}>
										We didn't find any invoice data. Try changing your filters or expanding the
										selected time period.
									</div>
								}
							/>
						) : (
							[
								<>
									<Row key={2} style={{ margin: '0.5em -8px' }} gutter={16}>
										<Col span={24}>
											<Card>
												<Spin spinning={spendCostsByVendor.loading || initialLoading}>
													{spendByVendorData && spendByVendorData.length > 0 ? (
														<Table
															pagination={printing ? false : { pageSize: 8 }}
															columns={spendByVendorColumns}
															rowKey={(el) => el['id']}
															dataSource={spendByVendorData}
														/>
													) : null}
												</Spin>
											</Card>
										</Col>
									</Row>
									<Row>
										<Col span={24}>
											<SpendBreakdownSection
												loading={spendCostsByVendor.loading || initialLoading}
												filters={vendorFilters}
												userType={userType}
												currency={currency}
												rows={spendByVendorData}
												activeSources={breakdownActiveSources}
												totalSources={INVOICE_ONLY_SOURCES}
												reportKey="spend_costs_by_supplier_facility"
												normalizeComparisonRows={(rawData) => rawData || []}
												categoryLabel="Supplier"
												titleLabel="Invoice Spend by Supplier"
												csvTitle={CSV_TITLE}
												exportValueLabel="Invoice Spend"
											/>
										</Col>
									</Row>
								</>,

								<Row key={3} style={{ margin: '0.5em -8px' }} gutter={16}>
									<Col span={24}>
										<SpendOverTimeChart
											monthlyData={spendCostsByMonth.data}
											currency={currency}
											loading={spendCostsByMonth.loading || initialLoading}
											allowedSources={INVOICE_ONLY_SOURCES}
										/>
									</Col>
								</Row>,
							]
						)}
					</div>
				) : (
					<EmptyState
						graphic={
							<img
								alt="You haven't activated your account yet."
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/letter owl.svg"
							/>
						}
						headline={"You haven't activated your account yet."}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 560, marginBottom: 16 }}>
									Please check your email. We sent you a note with an activation link.
								</div>
							</div>
						}
					/>
				)}
			</Content>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	accountActivated: state.session.accountActivated,
	currentUser: state.session.currentUser,
	analytics: state.spend_analytics,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	saveVendorFilters: (filters) => dispatch(updateVendorFilters(filters)),
	clearAllVendorFilters: (filters) => dispatch(clearVendorFilters()),
	fetchSpendData: (params, filters = null, pagination = null, sorting = null) =>
		dispatch(fetchSpendCostsByVendor(ownProps.userType)(params, filters, pagination, sorting)),
	fetchTimeSeriesSpendData: (params, filters = null, pagination = null, sorting = null) =>
		dispatch(fetchSpendCostsByMonth(ownProps.userType)(params, filters, pagination, sorting)),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(SpendAnalyticsOverviewVendorsPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
